// Pure math sequence and number tools. Number in, text out, entirely in the
// browser. BigInt keeps large results exact (tsconfig targets < ES2020, so use
// the BigInt() constructor, never 0n literals).

export type MathToolVariant =
  | 'fibonacci-generator'
  | 'factorial-calculator'
  | 'prime-number-generator'
  | 'pascals-triangle-generator';

export type MathToolMeta = {
  badge: string;
  title: string;
  inputLabel: string;
  default: string;
  compute: (n: number) => string;
};

const ZERO = BigInt(0);
const ONE = BigInt(1);

function clampCount(n: number, max: number): number {
  const value = Math.floor(n);
  if (!Number.isFinite(value) || value < 1) return 1;
  return Math.min(value, max);
}

// A silent cap answers a different question than the one asked, and the answer looks valid.
function capNote(asked: number, used: number, unit: string): string {
  const value = Math.floor(asked);
  if (!Number.isFinite(value) || value <= used) return '';
  return `\n\nCapped at ${used} ${unit}. You asked for ${value.toLocaleString('en-US')}.`;
}

function fibonacci(count: number): string {
  const total = clampCount(count, 200);
  const out: string[] = [];
  let a = ZERO;
  let b = ONE;
  for (let i = 0; i < total; i += 1) {
    out.push(a.toString());
    const next = a + b;
    a = b;
    b = next;
  }
  return out.join(', ') + capNote(count, 200, 'numbers');
}

function factorial(n: number): string {
  const value = Math.floor(n);
  if (!Number.isFinite(value) || value < 0) {
    return 'Enter a whole number of 0 or more.';
  }
  const capped = Math.min(value, 2000);
  let result = ONE;
  for (let i = 2; i <= capped; i += 1) {
    result *= BigInt(i);
  }
  return result.toString() + capNote(value, 2000, 'as the input');
}

function primes(count: number): string {
  const total = clampCount(count, 1000);
  const out: number[] = [];
  let candidate = 2;
  while (out.length < total) {
    let isPrime = true;
    for (let i = 0; i < out.length; i += 1) {
      const p = out[i];
      if (p * p > candidate) break;
      if (candidate % p === 0) {
        isPrime = false;
        break;
      }
    }
    if (isPrime) out.push(candidate);
    candidate += 1;
  }
  return out.join(', ') + capNote(count, 1000, 'primes');
}

function pascalsTriangle(rows: number): string {
  const total = clampCount(rows, 30);
  const lines: string[] = [];
  let row: bigint[] = [ONE];
  for (let r = 0; r < total; r += 1) {
    lines.push(row.map((value) => value.toString()).join(' '));
    const next: bigint[] = [ONE];
    for (let i = 0; i < row.length - 1; i += 1) {
      next.push(row[i] + row[i + 1]);
    }
    next.push(ONE);
    row = next;
  }
  return lines.join('\n') + capNote(rows, 30, 'rows');
}

export const mathToolMeta: Record<MathToolVariant, MathToolMeta> = {
  'fibonacci-generator': {
    badge: 'Fibonacci',
    title: 'Generate Fibonacci numbers',
    inputLabel: 'How many numbers',
    default: '15',
    compute: fibonacci,
  },
  'factorial-calculator': {
    badge: 'Factorial',
    title: 'Calculate a factorial',
    inputLabel: 'Number (n)',
    default: '10',
    compute: factorial,
  },
  'prime-number-generator': {
    badge: 'Primes',
    title: 'Generate prime numbers',
    inputLabel: 'How many primes',
    default: '20',
    compute: primes,
  },
  'pascals-triangle-generator': {
    badge: 'Pascal',
    title: "Generate Pascal's triangle",
    inputLabel: 'How many rows',
    default: '8',
    compute: pascalsTriangle,
  },
};

export function runMathTool(variant: MathToolVariant, n: number): string {
  return mathToolMeta[variant].compute(n);
}
